import React, { useState, useMemo } from 'react';
import { TextField, Autocomplete, CircularProgress, Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/api/fetchApi';

export const DEFAULT_TENANT_ID = '01HXYZ000000000000000001';

export interface TenantSelectorProps {
  value: string;
  onChange: (tenantId: string) => void;
}

export function TenantSelector({ value, onChange }: TenantSelectorProps) {
  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => fetchApi('/domains/tenants'),
    staleTime: 10 * 60 * 1000,
  });

  const options = useMemo(() => {
    if (!tenants || !Array.isArray(tenants)) {
      return [{ id: DEFAULT_TENANT_ID, name: 'Default Tenant' }];
    }
    return tenants.map((t: { _id: string; name?: string; tradingName?: string }) => ({
      id: t._id.replace(/^tenant:/, ''),
      name: t.name || t.tradingName || t._id,
    }));
  }, [tenants]);

  const selectedOption = options.find((o) => o.id === value) || options[0] || { id: value, name: value };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => `${option.name} (${option.id})`}
      value={selectedOption}
      onChange={(_, newValue) => {
        if (newValue) onChange(newValue.id);
      }}
      loading={isLoading}
      sx={{ minWidth: 300 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tenant"
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="text.secondary">{option.id}</Typography>
          </Box>
        </Box>
      )}
    />
  );
}
